"use client";
import React from "react";
import { useDropzone } from "react-dropzone";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import { useState } from "react";
import Image from "next/image";

interface DragAndDropDocumentFieldProps {
  name: string;
  label: string;
  accept: Record<string, string[]>;
  multiple?: boolean;
  promptText: string;
  onFileUpload: (files: File[]) => void;
  defaultValue?: string;
  contentType?: string;
}

const thumb = {
  display: "block",
  borderRadius: 2,
  border: "1px solid #eaeaea",
  marginBottom: 8,
  marginRight: 8,
  padding: 4,
};

export function DragAndDropDocumentField(props: DragAndDropDocumentFieldProps) {
  const [files, setFiles] = useState<Array<File & { preview: string }>>([]);
  const { name, onFileUpload, promptText, accept, defaultValue, contentType } =
    props;

  const { getRootProps, getInputProps } = useDropzone({
    accept,
    onDrop: (acceptedFiles) => {
      onFileUpload(acceptedFiles);
      setFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        ),
      );
    },
  });

  const thumbs = files.map((file) => (
    <div style={thumb} key={file.name}>
      {file.type.includes("image") ? (
        <div className="relative h-40 block">
          <Image
            alt={file.name}
            src={file.preview}
            fill={true}
            className="object-cover object-left"
            onLoad={() => {
              URL.revokeObjectURL(file.preview);
            }}
          />
        </div>
      ) : (
        <iframe
          src={file.preview}
          title={file.name}
          className="w-full h-40 pointer-events-none"
          frameBorder="0"
        />
      )}
    </div>
  ));

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "70px",
        alignItems: "center",
        padding: "20px",
        marginTop: "0.5rem",
        borderWidth: "2px",
        borderRadius: "10px",
        borderColor: "#eeeeee",
        borderStyle: "dashed",
        color: "#bdbdbd",
        outline: "none",
        transition: "border .24s ease-in-out",
        cursor: "pointer",
      }}
      {...getRootProps()}>
      {files.length === 0 && !defaultValue && (
        <div
          style={{
            maxHeight: "45px",
            maxWidth: "45px",
            width: "100%",
            height: "100%",
            marginBottom: "10px",
          }}>
          <PlusCircleIcon className="size-7" />
        </div>
      )}

      {files.length > 0 && (
        <div
          style={{
            marginBottom: "16px",
            width: "100%",
          }}>
          {thumbs}
        </div>
      )}
      {files.length === 0 && defaultValue && (
        <div style={{ marginBottom: "16px", width: "100%" }}>
          <div style={thumb}>
            {contentType === "pdf" ? (
              <div className="relative h-40 block">
                <iframe
                  src={defaultValue}
                  title="default-pdf"
                  className="w-full h-full pointer-events-none"
                />
              </div>
            ) : (
              <div className="relative h-40 block">
                <Image
                  alt={"thumbnail"}
                  src={defaultValue}
                  className="object-cover object-left"
                  fill={true}
                />
              </div>
            )}
          </div>
        </div>
      )}
      <input {...getInputProps()} name={name} title="dropzone-input" />
      <p className="text-sm text-gray-400">{promptText}</p>
    </div>
  );
}
